import { observer } from 'mobx-react';
import * as React from 'react';
import { Heading, Separator, Text } from '@teliaee/design-system';
import ConfirmationHandler from '../components/ConfirmationHandler';
import NoticeAuthUpgrade from '../components/NoticeAuthUpgrade';
import UiNotice from '../components/UiNotice';
import PanelSelector from '../panels/PanelSelector';
import SelectedMobilePanel from '../panels/SelectedMobilePanel';
import useStores from '../stores/UseStores';
import { FetchStatus } from '../util/ApiUtil';
import { useIsMobileHook } from '../util/hooks/useIsMobile';
import { isSmallModuleView } from '../util/ModuleUtil';
import PaddedSpinner from '../util/PaddedSpinner';
import WrapWithContainerInFullPage from '../wrapper/WrapWithContainerInFullPage';

const HomePage = observer((): React.JSX.Element => {
  const {
    homeStore: { status, homeResponse, isRedirecting, panels, selectedPanelInStorage, clearRedirectStatus, loadIndexData },
    translateStore: { translate },
    routeStore: { hash },
    uiStore,
    uiStore: { selectedTab, componentWidth, setComponentWidth },
  } = useStores();
  const { isMobile } = useIsMobileHook();
  const shouldShowMobileNavigation = isMobile || isSmallModuleView(componentWidth);
  const refForWidth = React.useRef<HTMLDivElement>(null);
  const [backToMethodSelectionClicked, setBackToMethodSelectionClicked] = React.useState<boolean>(false);

  React.useEffect(() => {
    clearRedirectStatus();
    loadIndexData();
  }, []);

  React.useLayoutEffect(() => {
    if (!refForWidth.current) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width > 0) {
        setComponentWidth(width);
      }
    });
    observer.observe(refForWidth.current);

    const initialWidth = refForWidth.current.offsetWidth;
    if (initialWidth > 0) {
      setComponentWidth(initialWidth);
    }
    return () => observer.disconnect();
  }, [uiStore]);

  const getCurrentTab = (): string => {
    if (panels.length === 1) {
      return panels[0].id;
    }
    if (selectedTab) {
      return selectedTab;
    }
    if (!backToMethodSelectionClicked) {
      return hash || selectedPanelInStorage || '';
    }
    return '';
  };

  const currentTab = React.useMemo(getCurrentTab, [panels, selectedTab, backToMethodSelectionClicked, selectedPanelInStorage]);

  const renderContent = () => {
    if (status === FetchStatus.FETCHING || isRedirecting) {
      return <PaddedSpinner />;
    }

    if (homeResponse.component) {
      return <ConfirmationHandler component={homeResponse.component} params={homeResponse.params} />;
    }

    if (status === FetchStatus.ERROR) {
      return (
        <WrapWithContainerInFullPage>
          <UiNotice />
        </WrapWithContainerInFullPage>
      );
    }

    if (shouldShowMobileNavigation && currentTab) {
      const selectedPanel = panels.find((panel) => panel.id === currentTab);

      if (selectedPanel) {
        return <SelectedMobilePanel panel={selectedPanel} setBackToMethodSelectionClicked={setBackToMethodSelectionClicked} />;
      }
    }

    return (
      <WrapWithContainerInFullPage>
        <Text>
          <NoticeAuthUpgrade />
          <Heading className={`h4${shouldShowMobileNavigation ? '' : ' text-center'}`}>{translate('label.intro')}</Heading>
          <Separator color={'invisible'} spacing={'small'} />
          <PanelSelector />
        </Text>
      </WrapWithContainerInFullPage>
    );
  };

  return (
    <>
      <div aria-hidden={true} ref={refForWidth} style={{ width: '100%', height: '1px', position: 'relative' }} />
      {renderContent()}
    </>
  );
});

export default HomePage;
